import { useFocusEffect } from 'expo-router';
import React, { useCallback, useState } from 'react';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import IconAvatar from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface Copy {
  text: string;
}

export interface OfferItem {
  id: string;
  iconUrl: string;
  title: Copy;
}

export interface MyOfferTileCardProps extends PressableAnalyticsProps {
  bodyItems: OfferItem[];
  titleText: string;
  captionText: string;
  isDisabled?: boolean;
  onCardPress: () => void;
  onSetIsPressed?: (isPressed: boolean) => void;
}

enum MyOfferTileCardState {
  DEFAULT = 'default',
  PRESSED = 'pressed',
  ACTIVE = 'active',
  DISABLED = 'disabled',
  HOVER = 'hover',
  FOCUSED = 'focused',
}

const TITLE_TEXT_LINE_LIMIT = 1;
const ACTIVATED_OFFER_TEXT_LINE_LIMIT = 1;

export const MyOfferTileCard = ({
  bodyItems,
  titleText,
  captionText,
  isDisabled = false,
  onCardPress,
  analyticsId = '',
  customAttributes = null,
}: MyOfferTileCardProps) => {
  const cardPressAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const [cardState, setCardState] = useState<MyOfferTileCardState>(() => {
    if (isDisabled) {
      return MyOfferTileCardState.DISABLED;
    }
    return MyOfferTileCardState.DEFAULT;
  });

  useFocusEffect(
    useCallback(() => {
      setCardState(
        isDisabled
          ? MyOfferTileCardState.DISABLED
          : MyOfferTileCardState.DEFAULT,
      );
    }, [isDisabled]),
  );

  const handlePressIn = useCallback(() => {
    if (cardState !== MyOfferTileCardState.DISABLED) {
      setCardState(MyOfferTileCardState.PRESSED);
    }
  }, [cardState]);

  const handlePressOut = useCallback(() => {
    if (cardState !== MyOfferTileCardState.DISABLED) {
      setCardState(MyOfferTileCardState.HOVER);
    }
  }, [cardState]);

  const handleHoverIn = useCallback(() => {
    if (
      cardState !== MyOfferTileCardState.DISABLED &&
      cardState !== MyOfferTileCardState.PRESSED
    ) {
      setCardState(MyOfferTileCardState.HOVER);
    }
  }, [cardState]);

  const handleHoverOut = useCallback(() => {
    if (
      cardState !== MyOfferTileCardState.DISABLED &&
      cardState !== MyOfferTileCardState.PRESSED
    ) {
      setCardState(MyOfferTileCardState.DEFAULT);
    }
  }, [cardState]);

  const handleFocus = useCallback(() => {
    if (cardState !== MyOfferTileCardState.DISABLED) {
      setCardState(MyOfferTileCardState.FOCUSED);
    }
  }, [cardState]);

  const handleBlur = useCallback(() => {
    if (cardState !== MyOfferTileCardState.DISABLED) {
      setCardState(MyOfferTileCardState.DEFAULT);
    }
  }, [cardState]);

  const handleOnPress = useCallback(() => {
    if (cardState !== MyOfferTileCardState.DISABLED) {
      cardPressAnalytics();
      onCardPress();
    }
  }, [cardState, onCardPress, cardPressAnalytics]);

  const getContainerStyle = useCallback((): StyleProp<ViewStyle> => {
    const containerStyles: StyleProp<ViewStyle>[] = [styles.container];

    switch (cardState) {
      case MyOfferTileCardState.PRESSED:
        containerStyles.push(styles.press);
        break;
      case MyOfferTileCardState.DISABLED:
        containerStyles.push(styles.disabled);
        break;
      case MyOfferTileCardState.HOVER:
        containerStyles.push(styles.hover);
        break;
      case MyOfferTileCardState.FOCUSED:
        containerStyles.push(styles.focus);
        break;
    }

    return containerStyles;
  }, [cardState]);

  return (
    <Pressable
      onPress={handleOnPress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      role="button"
      accessible
      style={getContainerStyle()}
      testID="Activate Offer Tile"
      disabled={isDisabled}
    >
      <View style={styles.mainContainer}>
        <View style={styles.offerItemsContainer}>
          {bodyItems.length > 0 ? (
            bodyItems.map((item, index) => (
              <View
                key={item.id}
                style={[
                  styles.offerItemWrapper,
                  styles.offerItemWithShadow,
                  index > 0 && styles.offerItemOverlap,
                  { zIndex: bodyItems.length - index },
                ]}
                accessibilityLabel={item.title.text}
              >
                <IconAvatar
                  variant="MD"
                  iconKey={!item.iconUrl ? 'STORE' : undefined}
                  defaultPlaceholderIcon="STORE"
                  imageSource={item.iconUrl ? { uri: item.iconUrl } : undefined}
                  iconAccessibilityLabel={item.title.text}
                />
              </View>
            ))
          ) : (
            <View style={[styles.offerItemWrapper]}>
              <IconAvatar
                variant="MD"
                iconKey="STORE"
                defaultPlaceholderIcon="STORE"
                imageSource={undefined}
              />
            </View>
          )}
        </View>
        <View style={styles.textContainer}>
          <BodyBaseText
            style={[
              styles.title,
              cardState === MyOfferTileCardState.DISABLED &&
                styles.disabledTitle,
            ]}
            numberOfLines={TITLE_TEXT_LINE_LIMIT}
            allowFontScaling
          >
            {titleText}
          </BodyBaseText>
          <BodyXSText
            style={[
              styles.captionText,
              cardState === MyOfferTileCardState.DISABLED &&
                styles.disabledCaption,
            ]}
            numberOfLines={ACTIVATED_OFFER_TEXT_LINE_LIMIT}
            allowFontScaling
          >
            {captionText}
          </BodyXSText>
        </View>
        <View
          style={[
            styles.iconContainer,
            cardState === MyOfferTileCardState.DISABLED && styles.disabledIcon,
          ]}
        >
          <Icon
            iconAccessibilityLabel="chevron-right-icon"
            iconKey="CHEVRON_RIGHT"
            iconSize="MD"
          />
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  captionText: {
    color: theme.color.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    borderRadius: theme.spacing.xs,
    marginHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.s,
    minHeight: 70,
  },
  disabled: {
    backgroundColor: theme.color.background.primary,
  },
  disabledCaption: {
    color: theme.color.text.tertiary,
  },
  disabledIcon: {
    opacity: 0.4,
  },
  disabledTitle: {
    color: theme.color.text.tertiary,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.border.focused,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing.l,
  },
  mainContainer: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  offerItemOverlap: {
    marginLeft: -24,
  },
  offerItemWrapper: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderRadius: theme.spacing.xs,
    height: 40,
    justifyContent: 'center',
  },
  offerItemWithShadow: {
    elevation: 2,
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
  },
  offerItemsContainer: {
    alignItems: 'center',
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
    borderColor: theme.color.border.primary,
  },
  textContainer: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
  },
  title: {
    color: theme.color.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default MyOfferTileCard;
